import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Ban, Flag, FlagOff, Pencil } from "lucide-react";

import { ApiError } from "../../lib/api";
import { Modal } from "../../components/Modal";
import { useToast } from "../../components/Toast";
import { Button, Input, Textarea, useDescarteProtegido, useHayCambios } from "../../components/ui";
import {
  anularLectura,
  corregirLectura,
  marcarRevision,
  type LecturaRead,
} from "./api";

/**
 * Acciones de la administradora sobre una lectura (recorrido R1).
 *
 * Hasta este lote el listado era sólo de consulta: detectado un error, la
 * única salida era pedirle al operador que volviera a pasar por la PWA.
 */
export function AccionesLectura({ lectura }: { lectura: LecturaRead }): JSX.Element {
  const [abierto, setAbierto] = useState<"corregir" | "anular" | "revision" | null>(
    null,
  );
  const editable = lectura.estado !== "Anulada" && lectura.estado !== "Pagada";

  return (
    <>
      <div className="flex items-center gap-0.5">
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          icon={Pencil}
          disabled={!editable}
          title={
            editable
              ? "Corregir la lectura (anula y reemplaza)"
              : "No se corrige una lectura pagada o anulada"
          }
          onClick={() => setAbierto("corregir")}
        >
          Corregir
        </Button>
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          icon={lectura.flag_revision ? FlagOff : Flag}
          disabled={lectura.estado === "Anulada"}
          title={
            lectura.flag_revision
              ? "Sacar la marca de revisión"
              : "Marcar para revisión"
          }
          onClick={() => setAbierto("revision")}
        >
          Revisión
        </Button>
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          icon={Ban}
          disabled={!editable}
          title={
            editable ? "Anular la lectura" : "No se anula una lectura pagada o anulada"
          }
          onClick={() => setAbierto("anular")}
        >
          Anular
        </Button>
      </div>

      {abierto === "corregir" && (
        <CorregirModal lectura={lectura} onClose={() => setAbierto(null)} />
      )}
      {abierto === "anular" && (
        <AnularModal lectura={lectura} onClose={() => setAbierto(null)} />
      )}
      {abierto === "revision" && (
        <RevisionModal lectura={lectura} onClose={() => setAbierto(null)} />
      )}
    </>
  );
}

function useInvalidar(): () => void {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ["lecturas"] });
    void qc.invalidateQueries({ queryKey: ["cierre"] });
    void qc.invalidateQueries({ queryKey: ["cierre-validaciones"] });
  };
}

function CorregirModal({
  lectura,
  onClose,
}: {
  lectura: LecturaRead;
  onClose: () => void;
}): JSX.Element {
  const toast = useToast();
  const invalidar = useInvalidar();
  const [valor, setValor] = useState(String(lectura.lectura_actual));
  const [motivo, setMotivo] = useState("");

  const nuevo = Number(valor);
  const consumo = Number.isFinite(nuevo)
    ? nuevo - lectura.lectura_anterior
    : null;

  const m = useMutation({
    mutationFn: () =>
      corregirLectura(lectura.id, { lectura_actual: nuevo, motivo: motivo.trim() }),
    onSuccess: (nueva) => {
      toast.success(
        `Lectura corregida: ${nueva.consumo_m3} m³ (la anterior queda anulada).`,
      );
      invalidar();
      onClose();
    },
    onError: (err: unknown) =>
      toast.error(
        err instanceof ApiError ? err.message : "No se pudo corregir la lectura",
      ),
  });

  const valido =
    Number.isFinite(nuevo) &&
    nuevo >= 0 &&
    nuevo !== lectura.lectura_actual &&
    motivo.trim().length >= 3;

  // Cambios sin guardar: cerrar pregunta antes de descartar (auditoría UX-01).
  const hayCambios = useHayCambios([valor, motivo]);
  const { cerrar: intentarCerrar, dialogo: dialogoDescarte } = useDescarteProtegido(
    hayCambios,
    onClose,
    m.isPending,
  );

  return (
    <Modal
      open
      onClose={intentarCerrar}
      title="Corregir lectura"
      subtitle={`Período ${String(lectura.mes).padStart(2, "0")}/${lectura.anio}`}
      icon={Pencil}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={intentarCerrar} disabled={m.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={!valido || m.isPending}
            onClick={() => m.mutate()}
          >
            {m.isPending ? "Corrigiendo…" : "Corregir lectura"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-neutral-600">
          La lectura actual no se edita: se anula y se crea una nueva con el
          valor correcto. Queda la cadena completa para auditoría.
        </p>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="rounded-md bg-neutral-50 p-3">
            <div className="text-xs uppercase tracking-wider text-neutral-500">
              Anterior
            </div>
            <div className="text-lg font-semibold tabular-nums">
              {lectura.lectura_anterior}
            </div>
          </div>
          <div className="rounded-md bg-neutral-50 p-3">
            <div className="text-xs uppercase tracking-wider text-neutral-500">
              Cargada
            </div>
            <div className="text-lg font-semibold tabular-nums">
              {lectura.lectura_actual}{" "}
              <span className="text-sm font-normal text-neutral-500">
                ({lectura.consumo_m3} m³)
              </span>
            </div>
          </div>
        </div>
        <label className="block">
          <span className="block text-xs font-medium text-neutral-700">
            Lectura correcta
          </span>
          <Input
            type="number"
            min={0}
            value={valor}
            autoFocus
            onChange={(e) => setValor(e.target.value)}
            className="mt-1"
          />
          {consumo !== null && (
            <span
              className={`mt-1 block text-xs ${
                consumo < 0 ? "text-rose-700" : "text-neutral-500"
              }`}
            >
              {consumo < 0
                ? `Quedaría ${consumo} m³: el medidor no puede retroceder.`
                : `Consumo resultante: ${consumo} m³`}
            </span>
          )}
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-neutral-700">
            Motivo de la corrección
          </span>
          <Textarea
            rows={2}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Ej: dígito de más al digitar desde la planilla"
            className="mt-1"
          />
        </label>
      </div>
      {dialogoDescarte}
    </Modal>
  );
}

function AnularModal({
  lectura,
  onClose,
}: {
  lectura: LecturaRead;
  onClose: () => void;
}): JSX.Element {
  const toast = useToast();
  const invalidar = useInvalidar();
  const [motivo, setMotivo] = useState("");

  const m = useMutation({
    mutationFn: () => anularLectura(lectura.id, motivo.trim()),
    onSuccess: () => {
      toast.success("Lectura anulada.");
      invalidar();
      onClose();
    },
    onError: (err: unknown) =>
      toast.error(
        err instanceof ApiError ? err.message : "No se pudo anular la lectura",
      ),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Anular lectura"
      subtitle={`Período ${String(lectura.mes).padStart(2, "0")}/${lectura.anio}`}
      icon={Ban}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={m.isPending}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            disabled={motivo.trim().length < 3 || m.isPending}
            onClick={() => m.mutate()}
          >
            {m.isPending ? "Anulando…" : "Anular lectura"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-neutral-600">
          El contrato queda sin lectura en el período (va a aparecer como
          faltante al prevalidar). Si el cierre ya estaba prevalidado o
          facturado, vuelve a ABIERTO.
          {lectura.numero_boleta && (
            <>
              {" "}
              La boleta <strong>{lectura.numero_boleta}</strong> queda anulada y
              su número, como hueco auditable.
            </>
          )}
        </p>
        <label className="block">
          <span className="block text-xs font-medium text-neutral-700">Motivo</span>
          <Textarea
            rows={2}
            value={motivo}
            autoFocus
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Ej: la lectura corresponde a otro arranque"
            className="mt-1"
          />
        </label>
      </div>
    </Modal>
  );
}

function RevisionModal({
  lectura,
  onClose,
}: {
  lectura: LecturaRead;
  onClose: () => void;
}): JSX.Element {
  const toast = useToast();
  const invalidar = useInvalidar();
  const [nota, setNota] = useState("");
  const prender = !lectura.flag_revision;

  const m = useMutation({
    mutationFn: () => marcarRevision(lectura.id, prender, nota.trim() || undefined),
    onSuccess: () => {
      toast.success(prender ? "Lectura marcada para revisión." : "Revisión resuelta.");
      invalidar();
      onClose();
    },
    onError: (err: unknown) =>
      toast.error(
        err instanceof ApiError ? err.message : "No se pudo marcar la lectura",
      ),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={prender ? "Marcar para revisión" : "Soltar la revisión"}
      icon={prender ? Flag : FlagOff}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={m.isPending}>
            Cancelar
          </Button>
          <Button variant="primary" disabled={m.isPending} onClick={() => m.mutate()}>
            {m.isPending
              ? "Guardando…"
              : prender
              ? "Marcar para revisión"
              : "Soltar la revisión"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-neutral-600">
          {prender
            ? "La marca viaja con la lectura hasta el cierre: la prevalidación la levanta como alerta ALT-002. No cambia montos ni estado."
            : "Se saca la marca y queda constancia de por qué."}
        </p>
        <label className="block">
          <span className="block text-xs font-medium text-neutral-700">
            Nota (opcional)
          </span>
          <Textarea
            rows={2}
            value={nota}
            autoFocus
            onChange={(e) => setNota(e.target.value)}
            placeholder={
              prender
                ? "Ej: el socio reclama que no estaba en la casa"
                : "Ej: se verificó en terreno"
            }
            className="mt-1"
          />
        </label>
      </div>
    </Modal>
  );
}
